import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageSquareText } from 'lucide-react';
import { post, mensagemDeErro } from '../nucleo/api';
import { nomeDaTela, rotaAtualDoLog } from '../nucleo/log-erros';
import { moduloDaRota } from '../nucleo/modulos-web';
import { Botao } from './base';
import { Modal } from './sobreposicao';
import { useToast } from './toast';

/**
 * Ajustes do CRM 27/10 (C7, C26, K5, F11; decisão 5): "Dê seu feedback", o modal único dos menus "mais opções" (Comercial,
 * empresa, negociação). O texto vai para o log do núcleo marcado como feedback (`POST /erros/feedback`) e aparece em Admin >
 * Logs de erros para a equipe. Uso: `const fb = useFeedback()`, o item de menu chama `fb.abrir()` e a tela renderiza `fb.dialogo`.
 */

export const ICONE_FEEDBACK = <MessageSquareText className="size-4" aria-hidden />;

export function DialogoFeedback({ aberto, aoFechar }: { aberto: boolean; aoFechar: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const id = useId();
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState('');
  const fechar = () => {
    setTexto('');
    setErro('');
    aoFechar();
  };
  const enviar = async () => {
    if (!texto.trim()) return setErro(t('feedback.vazio'));
    setEnviando(true);
    try {
      await post('/erros/feedback', {
        texto: texto.trim(),
        rota: rotaAtualDoLog(),
        tela: nomeDaTela(false) || undefined,
        modulo: moduloDaRota(location.pathname) ?? undefined,
      });
      toast.sucesso(t('feedback.obrigado'));
      fechar();
    } catch (e) {
      toast.erro(mensagemDeErro(e));
    } finally {
      setEnviando(false);
    }
  };
  return (
    <Modal
      aberto={aberto}
      aoFechar={fechar}
      titulo={t('feedback.titulo')}
      rodape={
        <>
          <Botao variante="fantasma" onClick={fechar}>
            {t('comum.cancelar')}
          </Botao>
          <Botao variante="primario" carregando={enviando} onClick={() => void enviar()} data-enviar-feedback="">
            {t('feedback.enviar')}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-1.5" data-dialogo-feedback="">
        <label htmlFor={id} className="text-sm font-medium text-tinta-1">
          {t('feedback.pergunta')}
        </label>
        <textarea
          id={id}
          rows={4}
          maxLength={1000}
          value={texto}
          aria-invalid={erro ? true : undefined}
          aria-describedby={`${id}-ajuda`}
          onChange={(e) => {
            setTexto(e.target.value);
            setErro('');
          }}
          className="w-full min-w-0 resize-y rounded-lg border border-borda-campo bg-campo px-3 py-2 text-sm text-tinta-1 focus:border-foco focus:outline-2 focus:outline-offset-0 focus:outline-foco"
        />
        <p id={`${id}-ajuda`} className={`text-xs ${erro ? 'text-perigo' : 'text-tinta-3'}`} role={erro ? 'alert' : undefined}>
          {erro || t('feedback.ajuda')}
        </p>
      </div>
    </Modal>
  );
}

export function useFeedback() {
  const [aberto, setAberto] = useState(false);
  return {
    abrir: () => setAberto(true),
    dialogo: <DialogoFeedback aberto={aberto} aoFechar={() => setAberto(false)} />,
  };
}
